import type { FacetValue, Result as SearchProduct, SearchFacet } from "@/generated/api/types.gen"

import type { useTranslations } from "next-intl"

export type { FacetValue, SearchFacet }

export type SearchAttributes = Record<string, string[]>

export interface SearchQuery {
  text: string
  categories: string[]
  collections: string[]
  attributes: SearchAttributes
  priceMin?: number
  priceMax?: number
  inStockOnly?: boolean
  sort: SearchSort
  limit: number
}

export type SearchSort = "relevance" | "price_asc" | "price_desc"

export interface SearchPageData {
  products: SearchProduct[]
  suggestions: string[]
  pageNumber: number
  totalCount: number
  isEmpty: boolean
  hasMorePages: boolean
  facets?: SearchFacet[]
  serveId?: string
}

export const SEARCH_PARAM_KEYS = {
  Q: "q",
  SORT: "ordenar",
  MIN_PRICE: "precio_min",
  MAX_PRICE: "precio_max",
  IN_STOCK: "en_stock",
  PER_PAGE: "por_pagina",
  CATEGORIES: "categorias",
  COLLECTIONS: "colecciones",
} as const

export const ATTRIBUTE_PARAM_PREFIX = "f_"

export const ATTRIBUTE_PARAM_ALIASES: Partial<Record<string, string>> = { brand: "marca", size: "talla", color: "color" }

export const withAttribute = (attributes: SearchAttributes, key: string, values: string[]): SearchAttributes => {
  if (values.length > 0) return { ...attributes, [key]: values }
  return Object.fromEntries(Object.entries(attributes).filter(([entry]) => entry !== key))
}

export const facetValueLabel = (facets: SearchFacet[] | undefined, key: string, value: string): string =>
  facets?.find((facet) => facet.key === key)?.values.find((option) => option.value === value)?.label ?? value

export const DEFAULT_SEARCH_QUERY: SearchQuery = {
  text: "",
  categories: [],
  collections: [],
  attributes: {},
  priceMin: undefined,
  priceMax: undefined,
  inStockOnly: undefined,
  sort: "relevance",
  limit: 72,
}

export const LISTING_DEFAULT_QUERY: Partial<SearchQuery> = { inStockOnly: true }

// Typesense rejects any search with per_page > 250 ("Only upto 250 hits can be fetched per page"),
// which surfaces as a 500 from the Nelo API. Never request more than this in a single page.
export const MAX_ITEMS_PER_PAGE = 250

interface SortOption {
  value: SearchSort
  label: string
  description: string
  analyticsValue: string
}

type SearchT = ReturnType<typeof useTranslations<"search">>

// SORT_OPTIONS text can't hold translated literals statically. Resolve via this helper at
// each render site instead.
export const getSortOptions = (t: SearchT): SortOption[] => [
  { value: "relevance", label: t("sort.relevanceLabel"), description: t("sort.relevanceDescription"), analyticsValue: "relevance" },
  { value: "price_asc", label: t("sort.priceAscLabel"), description: t("sort.priceAscDescription"), analyticsValue: "price_asc" },
  { value: "price_desc", label: t("sort.priceDescLabel"), description: t("sort.priceDescDescription"), analyticsValue: "price_desc" },
]
